import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Box, Button, Typography, alpha } from '@mui/material';
import {
  ReportProblem as PenaltySidebarIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Notes as NotesIcon,
  CalendarToday as CalendarTodayIcon,
  Flag as FlagIcon,
  Description as DescriptionIcon,
  Circle as CircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { format, isPast, differenceInCalendarDays } from 'date-fns';
import axiosInstance from '../../../utils/axios';
import { useAuth } from '../../../contexts/AuthContext';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import { canModifyPenalty } from '../../../utils/eventPenaltyPermissions';
import { injectDetailsPageStyles } from '../../common/DetailsPagePrimitives';
import SystemInformationSection from '../../common/SystemInformationSection';
import { resolveUserId, userDetailsPath } from '../../../utils/userDetailsLink';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const NEON        = '#38BDF8';
const NEON2       = '#818CF8';
const NEON3       = '#34D399';
const AMBER       = '#FBBF24';
const CORAL       = '#F87171';
const SURFACE_MID = '#0a1628';
const SURFACE_UP  = '#0f1f38';
const TEXT_BODY   = '#E2E8F0';
const TEXT_MUTED  = '#64748B';

// ─── Status config ─────────────────────────────────────────────────────────────
const STATUS_MAP = {
  pending:   { color: AMBER, label: 'Pending' },
  resolved:  { color: NEON3, label: 'Resolved' },
  overdue:   { color: CORAL, label: 'Overdue' },
  cancelled: { color: CORAL, label: 'Cancelled' },
};
const getStatus = (s) => STATUS_MAP[s?.toLowerCase()] ?? { color: NEON2, label: s || 'Unknown' };

// ─── Section wrapper ───────────────────────────────────────────────────────────
const Section = ({ title, icon: Icon, accent = NEON, delay = 0, children }) => (
  <Box sx={{
    borderRadius: '16px',
    border: `1px solid ${alpha(accent, 0.14)}`,
    background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
    overflow: 'hidden',
    animation: `fadeUp 0.4s ease ${delay}s both`,
    '@keyframes fadeUp': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to:   { opacity: 1, transform: 'translateY(0)' },
    },
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accent, 0.1)}`,
      background: alpha(accent, 0.04),
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        background: alpha(accent, 0.12), border: `1px solid ${alpha(accent, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 13, color: accent }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.07em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

// ─── Info row (ruled, label | value) ──────────────────────────────────────────
const InfoRow = ({ icon: Icon, label, value, accent = NEON, last = false }) => (
  <Box sx={{
    display: 'grid', gridTemplateColumns: '140px 1fr',
    alignItems: 'center', gap: 2, py: 1.1,
    borderBottom: last ? 'none' : `1px solid ${alpha(NEON, 0.05)}`,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
      {Icon && <Icon sx={{ fontSize: 12, color: alpha(accent, 0.45), flexShrink: 0 }} />}
      <Typography sx={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '0.62rem', fontWeight: 600,
        letterSpacing: '0.08em', textTransform: 'uppercase', color: TEXT_MUTED,
      }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{
      fontFamily: "'DM Sans', sans-serif",
      fontSize: '0.875rem', fontWeight: 500,
      color: value ? TEXT_BODY : TEXT_MUTED,
      fontStyle: value ? 'normal' : 'italic',
      lineHeight: 1.55,
    }}>
      {value || '—'}
    </Typography>
  </Box>
);

// ─── Prose block (for description / notes) ─────────────────────────────────────
const ProseBlock = ({ value, accent = NEON, emptyLabel = 'Nothing added yet.' }) => (
  <Box sx={{
    px: 2, py: 1.8, borderRadius: '8px',
    background: alpha(accent, 0.04),
    border: `1px solid ${alpha(accent, 0.1)}`,
    borderLeft: `3px solid ${alpha(accent, value ? 0.5 : 0.2)}`,
  }}>
    <Typography sx={{
      fontFamily: "'DM Sans', sans-serif",
      fontSize: '0.9rem', lineHeight: 1.75,
      color: value ? alpha(TEXT_BODY, 0.9) : TEXT_MUTED,
      fontStyle: value ? 'normal' : 'italic',
      whiteSpace: 'pre-wrap',
    }}>
      {value || emptyLabel}
    </Typography>
  </Box>
);

// ─── Metric card ───────────────────────────────────────────────────────────────
const MetricCard = ({ label, value, icon: Icon, color = NEON, sub, onClick }) => (
  <Box
    component={onClick ? 'button' : 'div'}
    type={onClick ? 'button' : undefined}
    onClick={onClick}
    sx={{
    position: 'relative', borderRadius: '14px', px: 2.5, py: 2,
    width: '100%',
    textAlign: 'left',
    background: `linear-gradient(145deg, ${alpha(color, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
    border: `1px solid ${alpha(color, 0.2)}`,
    boxShadow: `0 0 24px ${alpha(color, 0.07)}, inset 0 1px 0 ${alpha(color, 0.1)}`,
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'box-shadow 0.25s, border-color 0.25s, transform 0.15s ease',
    '&:hover': {
      boxShadow: `0 0 36px ${alpha(color, 0.16)}, inset 0 1px 0 ${alpha(color, 0.18)}`,
      borderColor: alpha(color, 0.35),
      ...(onClick ? { transform: 'translateY(-1px)' } : {}),
    },
  }}>
    <Box sx={{ position: 'absolute', top: -16, right: -16, width: 70, height: 70, borderRadius: '50%', background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: alpha(color, 0.75) }} />}
      <Typography sx={{ fontSize: '0.67rem', fontFamily: "'JetBrains Mono', monospace", color: TEXT_MUTED, letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, fontFamily: "'DM Sans', sans-serif", color, letterSpacing: 0.3, lineHeight: 1.15 }}>
      {value || '—'}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", mt: 0.4 }}>{sub}</Typography>}
  </Box>
);

// ═════════════════════════════════════════════════════════════════════════════
// Main Component
// ═════════════════════════════════════════════════════════════════════════════
const PenaltyDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [penalty, setPenalty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => { injectDetailsPageStyles(); }, []);

  useEffect(() => {
    const fetchPenalty = async () => {
      try {
        setLoading(true);
        const [penaltyRes, usersRes] = await Promise.all([
          axiosInstance.get(`/penalties/${id}`),
          axiosInstance.get('/users?minimal=1'),
        ]);
        setPenalty(penaltyRes.data);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.data || []);
      } catch {
        setError('Failed to load penalty details.');
      } finally {
        setLoading(false);
      }
    };
    fetchPenalty();
  }, [id]);

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/penalties/${id}`);
      setDeleteDialogOpen(false);
      setSuccess('Penalty deleted successfully.');
      setTimeout(() => navigate('/penalties'), 1200);
    } catch {
      setError('Failed to delete penalty.');
      setDeleteDialogOpen(false);
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message="Loading penalty details" size={56} variant="primary" />
    </Box>
  );
  if (!penalty) return null;

  // ─── Derived values ────────────────────────────────────────────────────────
  const canEdit     = canModifyPenalty(user, penalty);
  const getUserName = (uid) => users.find((u) => String(u.id) === String(uid))?.name || null;
  const statusCfg   = getStatus(penalty.status);

  const createdByName =
    penalty.createdBy?.name || penalty.creator?.name ||
    (typeof penalty.created_by === 'object' ? penalty.created_by?.name : null) ||
    getUserName(penalty.created_by) || null;

  const updatedByName =
    penalty.updatedBy?.name || penalty.updater?.name ||
    (typeof penalty.updated_by === 'object' ? penalty.updated_by?.name : null) ||
    getUserName(penalty.updated_by) || null;

  const assignedToName =
    penalty.assigned_to?.name || penalty.assignedTo?.name ||
    (typeof penalty.assigned_to_id === 'object' ? penalty.assigned_to_id?.name : null) ||
    getUserName(penalty.assigned_to_id) || null;
  const assignedToId = resolveUserId(penalty.assigned_to ?? penalty.assignedTo ?? penalty.assigned_to_id);
  const goToAssignedUser = assignedToId
    ? () => navigate(userDetailsPath(assignedToId))
    : undefined;

  // Due date urgency
  const dueDate      = penalty.due_date ? new Date(penalty.due_date) : null;
  const daysLeft     = dueDate ? differenceInCalendarDays(dueDate, new Date()) : null;
  const isOverdue    = dueDate && isPast(dueDate) && penalty.status?.toLowerCase() !== 'resolved';
  const isDueSoon    = !isOverdue && daysLeft !== null && daysLeft <= 3;
  const dueDateColor = isOverdue ? CORAL : isDueSoon ? AMBER : NEON3;
  const dueDateLabel = dueDate ? format(dueDate, 'EEE, dd MMM yyyy') : null;
  const urgencyNote  = isOverdue
    ? `Overdue by ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''}`
    : isDueSoon
      ? `Due in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`
      : daysLeft !== null
        ? `${daysLeft} days remaining`
        : null;

  const linkedEventId = penalty.event?.id ?? penalty.event_id;
  const linkedEventName = penalty.event?.name;
  const goToLinkedEvent = linkedEventId
    ? () => navigate(`/events/view/${linkedEventId}`)
    : undefined;

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 8 }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Header
        title="Penalty Details"
        subtitle="View penalty information"
        icon={PenaltySidebarIcon}
        showBackButton
        onBackClick={() => navigate('/penalties')}
        actionButton={canEdit ? (
          <Box sx={{ display: 'flex', gap: 1.2 }}>
            <Button
              startIcon={<EditIcon sx={{ fontSize: 15 }} />}
              onClick={() => navigate(`/penalties/edit/${id}`)}
              sx={{ borderRadius: '10px', px: 2.2, py: 0.65, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem', background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.55)}`, color: NEON, transition: 'all 0.2s', '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 14px ${alpha(NEON, 0.2)}` } }}
            >Edit</Button>
            <Button
              startIcon={<DeleteIcon sx={{ fontSize: 15 }} />}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{ borderRadius: '10px', px: 2.2, py: 0.65, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem', background: 'transparent', border: `1.5px solid ${alpha(CORAL, 0.55)}`, color: CORAL, transition: 'all 0.2s', '&:hover': { background: alpha(CORAL, 0.08), borderColor: CORAL, boxShadow: `0 0 14px ${alpha(CORAL, 0.2)}` } }}
            >Delete</Button>
          </Box>
        ) : null}
      />

      {/* ── Feedback ───────────────────────────────────────────────────────── */}
      <FeedbackMessage message={error}   severity="error"   onClose={() => setError(null)}   sx={{ mb: 2 }} />
      <FeedbackMessage message={success} severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* ── Urgency banner ─────────────────────────────────────────────────── */}
        {(isOverdue || isDueSoon) && penalty.status?.toLowerCase() !== 'resolved' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.4, borderRadius: '12px', background: alpha(dueDateColor, 0.07), border: `1px solid ${alpha(dueDateColor, 0.3)}`, animation: 'fadeUp 0.4s ease 0.05s both' }}>
            <WarningIcon sx={{ fontSize: 18, color: dueDateColor, flexShrink: 0 }} />
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', fontWeight: 600, color: dueDateColor }}>
              {isOverdue
                ? `This penalty is overdue by ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''}. Immediate attention required.`
                : `Due in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} — resolve this penalty soon.`}
            </Typography>
          </Box>
        )}

        {/* ── HERO ─────────────────────────────────────────────────────────── */}
        <Box sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(NEON, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
          p: { xs: 2, md: 2.5 },
          animation: 'fadeUp 0.35s ease both',
          '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        }}>
          {/* Title row + status badge */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '13px', flexShrink: 0, background: `linear-gradient(135deg, ${alpha(NEON2, 0.25)}, ${alpha(NEON, 0.15)})`, border: `2px solid ${alpha(NEON2, 0.3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PenaltySidebarIcon sx={{ fontSize: 22, color: NEON2 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, fontFamily: "'DM Sans', sans-serif", color: TEXT_BODY, letterSpacing: -0.3, lineHeight: 1.15 }}>
                  {penalty.title}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
                  {linkedEventName ? (
                    <Box
                      component="span"
                      onClick={goToLinkedEvent}
                      role={goToLinkedEvent ? 'link' : undefined}
                      tabIndex={goToLinkedEvent ? 0 : undefined}
                      onKeyDown={goToLinkedEvent ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToLinkedEvent(); } } : undefined}
                      sx={{
                        color: goToLinkedEvent ? NEON2 : 'inherit',
                        cursor: goToLinkedEvent ? 'pointer' : 'default',
                        textDecoration: goToLinkedEvent ? 'underline' : 'none',
                        textUnderlineOffset: '2px',
                        '&:hover': goToLinkedEvent ? { color: NEON } : {},
                      }}
                    >
                      {linkedEventName}
                    </Box>
                  ) : (
                    'No linked event'
                  )}
                  {assignedToName ? ` · ${assignedToName}` : ' · Unassigned'}
                </Typography>
              </Box>
            </Box>

            {/* Status badge — single source of truth for status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, px: 1.4, py: 0.5, borderRadius: '20px', background: alpha(statusCfg.color, 0.1), border: `1px solid ${alpha(statusCfg.color, 0.3)}`, alignSelf: 'flex-start' }}>
              <CircleIcon sx={{ fontSize: 7, color: statusCfg.color }} />
              <Typography sx={{ fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: statusCfg.color, letterSpacing: '0.05em' }}>
                {statusCfg.label}
              </Typography>
            </Box>
          </Box>

          {/* Metric strip — 3 cards: Assigned To, Due Date, Linked Event */}
          {/* Status removed here — already shown in the badge above */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            <MetricCard
              label="Assigned To"
              value={assignedToName || 'Unassigned'}
              icon={PersonIcon}
              color={NEON}
              onClick={goToAssignedUser}
              sub={goToAssignedUser ? 'View user details' : undefined}
            />
            <MetricCard label="Due Date"     value={dueDateLabel}                   icon={CalendarTodayIcon} color={dueDateColor} sub={urgencyNote} />
            <MetricCard
              label="Linked Event"
              value={linkedEventName || '—'}
              icon={EventIcon}
              color={NEON2}
              onClick={goToLinkedEvent}
              sub={goToLinkedEvent ? 'View event details' : undefined}
            />
          </Box>
        </Box>

        {/* ── Description & Notes (two-col) ──────────────────────────────────── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Section title="Description" icon={DescriptionIcon} accent={NEON2} delay={0.1}>
            <ProseBlock value={penalty.description} accent={NEON2} emptyLabel="No description added." />
          </Section>
          <Section title="Notes" icon={NotesIcon} accent={NEON3} delay={0.12}>
            <ProseBlock value={penalty.notes} accent={NEON3} emptyLabel="No notes added." />
          </Section>
        </Box>

        {/* ── System Information (UserDetails style) ───────────────────────── */}
        <SystemInformationSection
          createdBy={createdByName || 'Not available'}
          createdAt={penalty.created_at ? format(new Date(penalty.created_at), 'MMM d yyyy, HH:mm') : null}
          updatedBy={updatedByName || 'Not available'}
          updatedAt={penalty.updated_at ? format(new Date(penalty.updated_at), 'MMM d yyyy, HH:mm') : null}
          updatedAtLabel="Last Updated"
          delay={0.18}
          accentColor={NEON2}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        itemType="penalty"
        itemName={penalty?.title}
        description="This action cannot be undone and will permanently remove this penalty."
        confirmLabel="Delete Penalty"
      />
    </Container>
  );
};

export default PenaltyDetails;
